import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  CardActions
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Payment,
  Cancel,
  Info,
  Email,
  AdminPanelSettings,
  CreditCard,
  AccountBalance,
  Store,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import usePaymentProcessor from '../hooks/usePaymentProcessor';
import PaymentDialog from '../components/ui/PaymentDialog';
import { isWireTransferEnabled } from '../utils/featureFlags';
import axios from 'axios';
import api, { handleApiError } from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const RegistrationStatusPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAuth } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState(null);
  
  // Payment related state
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [success, setSuccess] = useState('');
  const [cancellingPayment, setCancellingPayment] = useState('');

  // Payment processor hook
  const paymentProcessor = usePaymentProcessor(
    paymentOptions,
    (message) => {
      setSuccess(message);
      // Refresh user data and registration status
      const refreshData = async () => {
        try {
          await checkAuth();
          await fetchRegistrationStatus();
        } catch (error) {
          console.error('Failed to refresh after payment:', error);
        }
      };
      
      refreshData();
      
      // Close dialog and reset forms after success
      setTimeout(() => {
        paymentProcessor.setDialogOpen(false);
        paymentProcessor.resetPaymentState();
        // Force a page refresh if the registration status hasn't updated
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }, 2000);
    },
    (errorMessage) => {
      setError(errorMessage);
    }
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchRegistrationStatus();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Load payment options if user needs to make payment
    if (user && user.registrationStatus === 'payment_pending') {
      loadPaymentOptions();
    }
  }, [user]);

  const fetchRegistrationStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registration/status`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setRegistrationStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching registration status:', error);
      setError(t('registrationStatus:errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending_verification':
        return 'warning';
      case 'pending_payment':
        return 'info';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'pending_verification':
        return <Schedule color="warning" />;
      case 'pending_payment':
        return <Payment color="info" />;
      case 'rejected':
        return <Cancel color="error" />;
      default:
        return <Info />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return t('registrationStatus:status.active');
      case 'pending_verification':
        return t('registrationStatus:status.pendingVerification');
      case 'pending_payment':
        return t('registrationStatus:status.pendingPayment');
      case 'rejected':
        return t('registrationStatus:status.rejected');
      default:
        return t('registrationStatus:status.inProgress');
    }
  };

  const loadPaymentOptions = async () => {
    try {
      const response = await api.get('/payment/options');
      if (response.data.success) {
        setPaymentOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error loading payment options:', error);
      setError(t('registrationStatus:errors.failedToLoadPaymentOptions'));
    }
  };

  const handleCancelPendingPayment = async (paymentToken) => {
    try {
      setCancellingPayment(paymentToken);
      const response = await api.delete(`/payment/${paymentToken}`);
      
      if (response.data.success) {
        setSuccess(t('registrationStatus:alerts.paymentCancelled'));
        // Refresh payment options to remove the cancelled payment
        await loadPaymentOptions();
      }
    } catch (error) {
      console.error('Error cancelling payment:', error);
      setError(handleApiError(error));
    } finally {
      setCancellingPayment('');
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('registrationStatus:loading')}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchRegistrationStatus}>
          {t('registrationStatus:buttons.retry')}
        </Button>
      </Container>
    );
  }

  if (!registrationStatus) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            {t('registrationStatus:noRegistration.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('registrationStatus:noRegistration.description')}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/email-verification')}>
            {t('registrationStatus:noRegistration.startRegistration')}
          </Button>
        </Paper>
      </Container>
    );
  }

  const { progress, overallStatus, selectedPlan, estimatedAmount, notifications, adminActions } = registrationStatus;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {getStatusIcon(overallStatus)}
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h4" gutterBottom>
              {t('registrationStatus:pageTitle')}
            </Typography>
            <Chip
              label={getStatusText(overallStatus)}
              color={getStatusColor(overallStatus)}
              size="large"
            />
          </Box>
        </Box>

        {/* Progress Overview */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('registrationStatus:sections.applicationProgress')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress.percentage}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('registrationStatus:progress.stepsCompleted', {
                  completedSteps: progress.completedSteps,
                  totalSteps: progress.totalSteps,
                  percentage: progress.percentage
                })}
              </Typography>
            </Box>
            
            <Grid container spacing={2} sx={{ width: '100%' }}>
              <Grid size={{xs:12 , sm:6}}>
                <Typography variant="body2" color="text.secondary">
                  {t('registrationStatus:labels.applicationId')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {registrationStatus.applicationId}
                </Typography>
              </Grid>
              {selectedPlan && (
                <>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('registrationStatus:labels.selectedPlan')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPlan.name}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('registrationStatus:labels.planPrice')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ₺{estimatedAmount}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Steps Detail */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('registrationStatus:sections.registrationSteps')}
            </Typography>
            <Stepper activeStep={progress.currentStep - 1} orientation="horizontal">
              {registrationStatus.steps.map((step) => (
                <Step key={step.stepName} completed={step.status === 'completed'}>
                  <StepLabel
                    error={step.status === 'failed'}
                    optional={
                      step.status === 'failed' && (
                        <Typography variant="caption" color="error">
                          {t('registrationStatus:labels.failed')}
                        </Typography>
                      )
                    }
                  >
                    {t(`registrationStatus:stepper.steps.${step.stepName}`, step.stepName.replace('_', ' ').toUpperCase())}
                  </StepLabel>
                  <StepContent>
                    {step.status === 'completed' && step.completedAt && (
                      <Typography variant="body2" color="text.secondary">
                        {t('registrationStatus:labels.completed')} {formatDate(step.completedAt)}
                      </Typography>
                    )}
                    {step.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {step.notes}
                      </Typography>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Current Status Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('registrationStatus:sections.currentStatus')}
            </Typography>
            
            {overallStatus === 'pending_verification' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('registrationStatus:alerts.pendingVerification.title')}</strong><br />
                  {t('registrationStatus:alerts.pendingVerification.description')}
                </Typography>
                {registrationStatus.estimatedProcessingTime && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('registrationStatus:alerts.pendingVerification.estimatedTime', { time: registrationStatus.estimatedProcessingTime })}
                  </Typography>
                )}
              </Alert>
            )}

            {overallStatus === 'pending_payment' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('registrationStatus:alerts.pendingPayment.title')}</strong><br />
                  {t('registrationStatus:alerts.pendingPayment.description', { amount: estimatedAmount })}
                </Typography>
                {registrationStatus.paymentDueDate && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('registrationStatus:alerts.pendingPayment.dueDate', { date: formatDate(registrationStatus.paymentDueDate) })}
                  </Typography>
                )}
              </Alert>
            )}

            {overallStatus === 'rejected' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('registrationStatus:alerts.rejected.title')}</strong><br />
                  {registrationStatus.rejectionReason || t('registrationStatus:alerts.rejected.description')}
                </Typography>
              </Alert>
            )}

            {overallStatus === 'completed' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('registrationStatus:accountStatus.active')}</strong><br />
                  {t('registrationStatus:accountStatus.congratulations')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/dashboard')}
                    color="primary"
                  >
                    Go to Dashboard
                  </Button>
                </Box>
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('registrationStatus:labels.nextAction')} {registrationStatus.nextAction}
            </Typography>
          </CardContent>
        </Card>

        {/* Payment Methods - Show only when payment is pending */}
        {overallStatus === 'pending_payment' && paymentOptions && (
          <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" gutterBottom>
                {t('registrationStatus:payment.completeYourPayment')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('registrationStatus:payment.choosePaymentMethod')}
              </Typography>
            </Box>

            {/* Payment Summary */}
            <Paper sx={{ p: 3, mb: 4, bgcolor: 'primary.50' }}>
              <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="h6" color="primary">
                    {paymentOptions.registration.selectedPlan.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Application ID: {paymentOptions.registration.applicationId}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="h4" color="primary">
                    {t('registrationStatus:payment.paymentSummary.amount', { amount: paymentOptions.registration.amount })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('registrationStatus:labels.oneTimePayment')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Error/Success Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Payment Success */}
            {paymentProcessor.paymentResult && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>{t('registrationStatus:paymentSuccess.title')}</Typography>
                <Typography variant="body2">
                  {t('registrationStatus:paymentSuccess.transactionId', { transactionId: paymentProcessor.paymentResult.payment.transactionId })}
                </Typography>
                <Typography variant="body2">
                  {t('registrationStatus:paymentSuccess.membershipActive')}
                </Typography>
              </Alert>
            )}

            {/* Payment Method Selection Title */}
            {!paymentProcessor.paymentResult && (
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                {(paymentOptions.pendingPayments && paymentOptions.pendingPayments.length > 0)
                  ? t('registrationStatus:labels.changePaymentMethod')
                  : t('registrationStatus:labels.selectPaymentMethod')
                }
              </Typography>
            )}

              {/* Pending Payments - Show at top */}
              {paymentOptions.pendingPayments && paymentOptions.pendingPayments.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon /> {t('registrationStatus:labels.activePendingPayment')}
                  </Typography>
                  {paymentOptions.pendingPayments.map((payment) => (
                    <Box key={payment.paymentToken} sx={{ mb: 2 }}>
                      <Alert 
                        severity="warning" 
                        sx={{ mb: 2 }}
                        action={
                          <Button
                            color="inherit"
                            size="small"
                            onClick={() => handleCancelPendingPayment(payment.paymentToken)}
                            disabled={cancellingPayment === payment.paymentToken}
                            startIcon={cancellingPayment === payment.paymentToken ? <CircularProgress size={16} /> : null}
                          >
                            {cancellingPayment === payment.paymentToken ? t('registrationStatus:labels.cancelling') : t('registrationStatus:buttons.cancelPayment')}
                          </Button>
                        }
                      >
                        <Typography variant="body2">
                          <strong>{payment.method.replace('_', ' ').toUpperCase()}</strong> payment for ₺{payment.amount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('registrationStatus:payment.paymentSummary.status', {
                            status: payment.status,
                            token: payment.paymentToken,
                            expires: new Date(payment.expiresAt).toLocaleString()
                          })}
                        </Typography>
                      </Alert>
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {t('registrationStatus:alerts.pendingPaymentDescription')}
                  </Typography>
                </Paper>
              )}

            {/* Payment Method Cards - Only show if no pending payments */}
            {!paymentProcessor.paymentResult && (!paymentOptions.pendingPayments || paymentOptions.pendingPayments.length === 0) && (
              <Grid container spacing={3} sx={{ width: '100%' }}>
                {paymentOptions.paymentMethods
                  .filter(method => method.method !== 'wire_transfer' || isWireTransferEnabled())
                  .map((method) => (
                  <Grid size={{ xs: 12, md: 4 }} key={method.method}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: method.available ? 'pointer' : 'not-allowed',
                        opacity: method.available ? 1 : 0.6,
                        '&:hover': method.available ? {
                          boxShadow: 4,
                          transform: 'translateY(-2px)'
                        } : {},
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => method.available && paymentProcessor.handleMethodSelect(method.method)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Box sx={{ fontSize: 48, mb: 2 }}>
                          {method.method === 'credit_card' && <CreditCard sx={{ fontSize: 48, color: 'primary.main' }} />}
                          {method.method === 'wire_transfer' && <AccountBalance sx={{ fontSize: 48, color: 'secondary.main' }} />}
                          {method.method === 'in_place' && <Store sx={{ fontSize: 48, color: 'success.main' }} />}
                        </Box>
                        
                        <Typography variant="h6" gutterBottom>
                          {t(`registrationStatus:payment.methodLabels.${method.method}`, method.name)}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {method.description}
                        </Typography>
                        
                        <Chip
                          label={t(`registrationStatus:payment.processingTimes.${method.processingTime}`, method.processingTime)}
                          size="small"
                          color={method.method === 'credit_card' ? 'success' : 'default'}
                        />
                      </CardContent>
                      
                      <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button
                          variant="contained"
                          disabled={!method.available || paymentProcessor.actionLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            paymentProcessor.handleMethodSelect(method.method);
                          }}
                          startIcon={paymentProcessor.actionLoading && paymentProcessor.selectedMethod === method.method ? <CircularProgress size={16} /> : null}
                        >
                          {paymentProcessor.actionLoading && paymentProcessor.selectedMethod === method.method ? t('registrationStatus:labels.processing') : t('registrationStatus:buttons.select')}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

          </Paper>
        )}

        {/* Recent Notifications */}
        {notifications && notifications.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('registrationStatus:sections.recentNotifications')}
              </Typography>
              <List>
                {notifications.map((notification, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        <Email color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.subject}
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {notification.message}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(notification.sentAt)} • {notification.type.toUpperCase()}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Admin Actions History */}
        {adminActions && adminActions.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('registrationStatus:sections.adminActions')}
              </Typography>
              <List>
                {adminActions.map((action, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        <AdminPanelSettings color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={action.action.replace('_', ' ').toUpperCase()}
                        secondary={
                          <>
                            {action.notes && (
                              <>
                                <Typography variant="body2" component="span">
                                  {action.notes}
                                </Typography>
                                <br />
                              </>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(action.timestamp)}
                              {action.admin && ` • By ${action.admin.firstName} ${action.admin.lastName}`}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < adminActions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button variant="outlined" onClick={fetchRegistrationStatus} sx={{ mr: 2 }}>
            {t('registrationStatus:buttons.refreshStatus')}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/profile')}>
            {t('registrationStatus:buttons.updateProfile')}
          </Button>
        </Box>

        {/* Payment Dialogs */}
        <PaymentDialog
          selectedMethod={paymentProcessor.selectedMethod}
          dialogOpen={paymentProcessor.dialogOpen}
          setDialogOpen={paymentProcessor.setDialogOpen}
          actionLoading={paymentProcessor.actionLoading}
          redirectData={paymentProcessor.redirectData}
          creditCardForm={paymentProcessor.creditCardForm}
          setCreditCardForm={paymentProcessor.setCreditCardForm}
          wireTransferInfo={paymentProcessor.wireTransferInfo}
          inPlaceToken={paymentProcessor.inPlaceToken}
          paymentOptions={paymentOptions}
          handleCreditCardPayment={paymentProcessor.handleCreditCardPayment}
          handleCardNumberChange={paymentProcessor.handleCardNumberChange}
          copyToClipboard={paymentProcessor.copyToClipboard}
          formatCardNumber={paymentProcessor.formatCardNumber}
        />
      </Paper>
    </Container>
  );


};

export default RegistrationStatusPage;